/**
 * Ticket Type Icon Component
 * Displays an appropriate icon for each ticket type
 */

import { Box, Tooltip } from '@mui/material'
import {
  BugReport,
  Lightbulb,
  HelpOutline,
  Build,
  Person,
  Flag,
  Warning,
  MoreHoriz,
  Report,
  ContentCopy,
  Copyright,
  Home,
  LocationOff,
  Block,
  HealthAndSafety,
  AddPhotoAlternate,
  PersonPinCircle,
  MyLocation,
  Schedule,
  FactCheck,
  EventBusy,
  Gavel,
} from '@mui/icons-material'
import { TicketType, TicketTypeLabels } from '@api/types/tickets'

interface TicketTypeIconProps {
  type: TicketType
  size?: 'small' | 'medium' | 'large'
  showLabel?: boolean
  color?: string
}

const typeIcons: Record<TicketType, React.ReactNode> = {
  [TicketType.ContentInappropriate]: <Flag />,
  [TicketType.ContentSpam]: <Report />,
  [TicketType.ContentDuplicate]: <ContentCopy />,
  [TicketType.ContentVandalism]: <Warning />,
  [TicketType.ContentCopyright]: <Copyright />,
  [TicketType.LocationPrivateProperty]: <Home />,
  [TicketType.LocationMissing]: <LocationOff />,
  [TicketType.LocationInaccessible]: <Block />,
  [TicketType.LocationSafety]: <HealthAndSafety />,
  [TicketType.LocationOutdatedInfo]: <EventBusy />,
  [TicketType.Bug]: <BugReport />,
  [TicketType.FeatureRequest]: <Lightbulb />,
  [TicketType.Question]: <HelpOutline />,
  [TicketType.AccountIssue]: <Person />,
  [TicketType.TechnicalSupport]: <Build />,
  [TicketType.ValidateCoverPhoto]: <AddPhotoAlternate />,
  [TicketType.ValidateOnSite]: <PersonPinCircle />,
  [TicketType.ValidateCoordinates]: <MyLocation />,
  [TicketType.ValidateHoursAccess]: <Schedule />,
  [TicketType.ValidateContent]: <FactCheck />,
  [TicketType.AdventureOwnershipClaim]: <Gavel />,
  [TicketType.Other]: <MoreHoriz />,
}

const typeColors: Record<TicketType, string> = {
  [TicketType.ContentInappropriate]: '#f44336',
  [TicketType.ContentSpam]: '#ff9800',
  [TicketType.ContentDuplicate]: '#9e9e9e',
  [TicketType.ContentVandalism]: '#e91e63',
  [TicketType.ContentCopyright]: '#795548',
  [TicketType.LocationPrivateProperty]: '#ff5722',
  [TicketType.LocationMissing]: '#607d8b',
  [TicketType.LocationInaccessible]: '#9c27b0',
  [TicketType.LocationSafety]: '#d32f2f',
  [TicketType.LocationOutdatedInfo]: '#8d6e63',
  [TicketType.Bug]: '#f44336',
  [TicketType.FeatureRequest]: '#ffeb3b',
  [TicketType.Question]: '#2196f3',
  [TicketType.AccountIssue]: '#009688',
  [TicketType.TechnicalSupport]: '#3f51b5',
  // Validation items share one teal family: they are to-dos, not alarms.
  [TicketType.ValidateCoverPhoto]: '#00897b',
  [TicketType.ValidateOnSite]: '#00796b',
  [TicketType.ValidateCoordinates]: '#0097a7',
  [TicketType.ValidateHoursAccess]: '#00838f',
  [TicketType.ValidateContent]: '#26a69a',
  [TicketType.AdventureOwnershipClaim]: '#5e35b1',
  [TicketType.Other]: '#9e9e9e',
}

const sizeMap = {
  small: 18,
  medium: 24,
  large: 32,
}

export function TicketTypeIcon({ type, size = 'medium', showLabel = false, color }: TicketTypeIconProps) {
  // A type this build does not know yet (the API can add one before the portal ships) still
  // renders, as Other, rather than as an empty box.
  const label = TicketTypeLabels[type] ?? TicketTypeLabels[TicketType.Other]
  const iconColor = color || typeColors[type] || typeColors[TicketType.Other]
  const iconSize = sizeMap[size]

  const icon = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: showLabel ? 1 : 0,
        color: iconColor,
        '& svg': {
          fontSize: iconSize,
        },
      }}
      aria-label={`Type: ${label}`}
    >
      {typeIcons[type] ?? typeIcons[TicketType.Other]}
      {showLabel && <span>{label}</span>}
    </Box>
  )

  if (showLabel) {
    return icon
  }

  return (
    // A tap reveals the type on a phone; lists that need it always visible pass showLabel.
    <Tooltip title={label} arrow enterTouchDelay={0} leaveTouchDelay={3000}>
      {icon}
    </Tooltip>
  )
}
